// layerOrderingHelpers.ts - Helper functions for managing layer ordering

import { Settings } from '@ais-ui/websockets';
import { LayerOrder } from './types';

export const addToLayerOrdering = (
  settings: Settings,
  layerType: string,
  layerId: string,
  position?: number
) => {
  const currentOrdering =
    settings['ribbonPlugin.settings']?.layer_ordering?.layers || [];
  const newLayer: LayerOrder = { type: layerType, id: layerId, enabled: true };

  if (position !== undefined) {
    currentOrdering.splice(position, 0, newLayer);
  } else {
    currentOrdering.push(newLayer); // Add to top by default
  }

  return {
    ...settings,
    'ribbonPlugin.settings': {
      ...settings['ribbonPlugin.settings'],
      layer_ordering: {
        version: 1,
        layers: currentOrdering,
      },
    },
  };
};

export const removeFromLayerOrdering = (
  settings: Settings,
  layerId: string
) => {
  const currentOrdering: LayerOrder[] =
    settings['ribbonPlugin.settings']?.layer_ordering?.layers || [];

  return {
    ...settings,
    'ribbonPlugin.settings': {
      ...settings['ribbonPlugin.settings'],
      layer_ordering: {
        version: 1,
        layers: currentOrdering.filter((layer) => layer.id !== layerId),
      },
    },
  };
};

// Add layer just below the ships layer (ships layer stays on top visually)
export const addLayerBelowShips = (
  settings: Settings,
  layerType: string,
  layerId: string
) => {
  const currentOrdering: LayerOrder[] =
    settings['ribbonPlugin.settings']?.layer_ordering?.layers || [];

  // Find the ships layer index
  const shipsIndex = currentOrdering.findIndex(
    (layer) => layer.type === 'ships'
  );

  if (shipsIndex === -1) {
    // No ships layer found, add the new layer first, then ships
    // This way ships will be on top visually
    const newOrdering = [
      { type: layerType, id: layerId, enabled: true },
      { type: 'ships', id: 'ships-layer', enabled: true },
    ];

    return {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        layer_ordering: {
          version: 1,
          layers: newOrdering,
        },
      },
    };
  } else {
    // Insert the new layer BEFORE the ships layer (at shipsIndex position)
    // This pushes ships to a higher index, making ships appear on top
    return addToLayerOrdering(settings, layerType, layerId, shipsIndex);
  }
};

// Update favorite shapes and layer ordering together
export const addShapeToFavoritesWithOrdering = (
  settings: Settings,
  shapeId: string
) => {
  // First, add to favorite shapes
  const updatedSettings = {
    ...settings,
    'ribbonPlugin.settings': {
      ...settings['ribbonPlugin.settings'],
      favorite_shapes: {
        enabled: false,
        shapes: [
          ...(settings['ribbonPlugin.settings']?.favorite_shapes?.shapes || []),
          shapeId,
        ],
      },
    },
  };

  // Then add to layer ordering below ships
  return addLayerBelowShips(updatedSettings, 'shape', shapeId);
};

// Remove favorite shape and from layer ordering
export const removeShapeFromFavoritesWithOrdering = (
  settings: Settings,
  shapeId: string
) => {
  // First, remove from favorite shapes
  const updatedSettings = {
    ...settings,
    'ribbonPlugin.settings': {
      ...settings['ribbonPlugin.settings'],
      favorite_shapes: {
        enabled: false,
        shapes:
          settings['ribbonPlugin.settings']?.favorite_shapes?.shapes?.filter(
            (id: string) => id !== shapeId
          ) || [],
      },
    },
  };

  // Then, remove from layer ordering
  return removeFromLayerOrdering(updatedSettings, shapeId);
};

// Helper functions for WMS layers
export const addWmsToFavoritesWithOrdering = (
  settings: Settings,
  wmsLayerId: string
) => {
  // Get the WMS infos from settings to find and enable the layer
  const wmsInfos = settings.wmsSettings || [];

  // Find the WMS layer and enable it
  const updatedWmsInfos = wmsInfos.map((wmsLayer: any) => {
    if (wmsLayer.layers) {
      return {
        ...wmsLayer,
        layers: wmsLayer.layers.map((layer: any) =>
          layer.id === wmsLayerId ? { ...layer, enabled: true } : layer
        ),
      };
    }
    return wmsLayer;
  });

  const updatedSettings = {
    ...settings,
    wmsSettings: updatedWmsInfos,
  };

  // Add to layer ordering below ships
  return addLayerBelowShips(updatedSettings, 'wms', wmsLayerId);
};

export const removeWmsFromFavoritesWithOrdering = (
  settings: Settings,
  wmsLayerId: string
) => {
  // Get the WMS infos from settings to find and disable the layer
  const wmsInfos = settings.wmsSettings || [];

  // Find the WMS layer and disable it
  const updatedWmsInfos = wmsInfos.map((wmsLayer: any) => {
    if (wmsLayer.layers) {
      return {
        ...wmsLayer,
        layers: wmsLayer.layers.map((layer: any) =>
          layer.id === wmsLayerId ? { ...layer, enabled: false } : layer
        ),
      };
    }
    return wmsLayer;
  });

  const updatedSettings = {
    ...settings,
    wmsSettings: updatedWmsInfos,
  };

  // Remove from layer ordering
  return removeFromLayerOrdering(updatedSettings, wmsLayerId);
};

// Helper for coverage layers
export const toggleCoverageWithOrdering = (
  settings: Settings,
  enabled: boolean
) => {
  const updatedSettings = {
    ...settings,
    'ribbonPlugin.settings': {
      ...settings['ribbonPlugin.settings'],
      coverage_source_identifiers: enabled, // Match your actual settings structure
    },
  };

  if (enabled) {
    return addLayerBelowShips(
      updatedSettings,
      'coverage',
      'source-identifiers'
    );
  } else {
    return removeFromLayerOrdering(updatedSettings, 'source-identifiers');
  }
};
